import { useContext, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import * as Sentry from '@sentry/react';
import { applicationsApi } from '../../../../api/applications';
import { resolveFinance } from './resolveFinance';
import { useSearchParams } from 'react-router-dom';
import { creditCardsApi } from '../../../../api/creditCards';
import { customersApi } from '../../../../api/customers';
import { paymentsApi } from '../../../../api/payments';
import { ConfirmPaymentDto } from '../../../../api/payments/types/confirm-payment.dto';
import { IPayment } from '../../../../api/payments/types/payment.type';
import { subscriptionsApi } from '../../../../api/subscriptions';
import { ApplicationContext } from '../../../../contexts/ApplicationContext';
import { BillingType } from '../constants/billingType';
import { CheckoutPageContext } from '../Context/CheckoutPageContext';
import { ContentContext } from '../Context/ContentContext';
import { CouponContext } from '../Context/CouponContext';
import { OrderBumpContext } from '../Context/OrderBumpContext';
import { PaymentContext } from '../Context/PaymentContext';
import { PlanContext } from '../Context/PlanContext';
import { SubscriptionContext } from '../Context/SubscriptionContext';
import { Address, CreditCard, Inputs } from '../Form/inputs';
import { reportCheckoutError } from '../../../../utils/sentry-checkout';
import { isCheckoutInputError } from '../../../../utils/checkout-input-error';

interface IHandleSubmit extends Inputs {
	billingType: number;
	/**
	 * Pagamento/assinatura recém-abertos, vindos DIRETO de quem acabou de criá-los.
	 *
	 * O contexto não serve aqui: `setPayment`/`setSubscription` são setState, e o
	 * valor só existe no próximo render — mas o `handle` deste submit já capturou
	 * o valor anterior no closure. Quando o comprador preenchia os campos e
	 * clicava em "Finalizar" sem antes tirar o foco deles, a abertura acontecia
	 * dentro do próprio submit e o `handle` ainda lia `null`: estourava
	 * PAYMENT_NOT_FOUND, limpava o storage e recarregava a página.
	 */
	opened?: {
		payment?: IPayment | null;
		subscription?: { id: number } | null;
	};
}

export enum Errors {
	PAYMENT_NOT_FOUND = 'PAYMENT_NOT_FOUND'
}

export const useSubmit = () => {
	const [searchParams] = useSearchParams();

	const { orderBumps } = useContext(OrderBumpContext);
	const { finance } = useContext(ApplicationContext);
	const queryClient = useQueryClient();

	// Gateway garantido antes de tokenizar (ver resolveFinance).
	const resolveCheckoutFinance = () =>
		resolveFinance(
			finance,
			() =>
				queryClient.fetchQuery(
					[applicationsApi.getFinanceParams.key],
					applicationsApi.getFinanceParams.fetch,
					{ retry: 2, staleTime: 0 }
				),
			(error) =>
				Sentry.captureException(error, {
					tags: { context: 'checkout:finance-params-before-tokenize' }
				})
		);
	// Página do builder pode desligar o endereço só neste checkout.
	const pageSettings = useContext(CheckoutPageContext);
	const isAddressRequired =
		pageSettings.fields?.address === 'hidden'
			? false
			: !!finance?.isAddressRequired;
	const { plan } = useContext(PlanContext);
	const { content, quantity } = useContext(ContentContext);
	const { coupon } = useContext(CouponContext);
	const { payment } = useContext(PaymentContext);
	const { subscription } = useContext(SubscriptionContext);

	const createSubscriptionApi = useMutation(subscriptionsApi.create.fetch);
	const confirmPaymentApi = useMutation(paymentsApi.confirm);
	const createCustomerApi = useMutation(customersApi.create);
	const tokenizeCreditCardApi = useMutation(creditCardsApi.tokenize);

	const handle = async ({
		creditCards,
		address,
		billingType,
		isForeign,
		opened,
		...data
	}: IHandleSubmit) => {
		// O que acabou de ser aberto ganha do contexto — ver `opened`.
		const currentPayment = opened?.payment ?? payment;
		const currentSubscription = opened?.subscription ?? subscription;

		if (!currentPayment && !plan) {
			throw new Error(Errors.PAYMENT_NOT_FOUND);
		}

		if (address) {
			// Espaço sobrando (autofill, colar) é recusado pelo gateway; o fluxo
			// de plano já cortava só o número, o de produto avulso não cortava nada.
			(Object.keys(address) as (keyof typeof address)[]).forEach((key) => {
				address[key] = address[key]?.trim();
			});
			address.postalcode = address.postalcode.replace(/\D/g, '');
		}

		// Mesmo motivo: o split por ' ' com espaço no fim ou duplo deixava o
		// número vazio ou o DDD errado. A validação estrita só roda no blur.
		data.phone = data.phone.trim().replace(/\s+/g, ' ');
		const [countryCode, areaCode, number] = data.phone.split(' ');
		const cpfCnpj = data.cpfCnpj.replace(/\D/g, '');

		await createCustomerApi.mutateAsync({
			name: data.name,
			email: data.email,
			isForeign: isForeign ?? false,
			cpfCnpj,

			address: isAddressRequired
				? {
						country: address.country,
						state: address.state,
						city: address.city,
						province: address.province,
						postalcode: address.postalcode,
						address: address.address,
						number: address.number,
						complement: address.complement
				  }
				: undefined,

			phones: {
				mobilePhone: {
					countryCode,
					areaCode,
					number
				}
			}
		});

		if (!content && !plan) throw new Error('Conteúdo inválido.');

		const getInstallmentCount = () => {
			if (!data.installmentCount) return 1;

			if (content && !content?.pricing.isInstallmentEnabled) return 1;

			if (billingType === BillingType.CreditCard)
				return data.installmentCount;

			if (!finance?.isBoletoNPixInstallmentEnabled) return 1;

			return data.installmentCount;
		};

		const installmentCount = getInstallmentCount();

		const createPaymentData: ConfirmPaymentDto = {
			quantity,
			paymentId: 1,
			billingTypeId: billingType,
			phone: data.phone,
			address: isAddressRequired ? address : undefined,
			contentId: content?.id || 1,
			cpfCnpj,
			coupon_code: coupon?.code,
			upsell_code: searchParams.get('upsell_code') || undefined,
			affiliate_code: searchParams.get('affiliate_code') || undefined,
			click: searchParams.get('click') || undefined,
			installmentCount,
			// Uuids das OFERTAS selecionadas (blocos do builder e/ou o bump
			// nativo da query) — o backend soma cada uma no pedido.
			order_bumps: orderBumps.map((bump) => bump.uuid),
			order_bump: orderBumps[0]?.uuid,
			sec: {
				turnstile: data.sec?.turnstile || undefined
			}
		};

		const isFree =
			(content?.pricing.price || 0) - (coupon?.flatDiscount || 0) === 0 &&
			content;

		const isCreditCard = billingType === BillingType.CreditCard;

		if (isCreditCard && !isFree) {
			createPaymentData.creditCards = await Promise.all(
				handleCreditCard(creditCards, address, isForeign, cpfCnpj)
			);
		}

		// Kept outside the card block: nested there, Pix fell through to the
		// PAYMENT_NOT_FOUND below, since the plan flow has no `payment`.
		if (plan) {
			if (isCreditCard && !createPaymentData.creditCards?.[0]) {
				throw new Error('Cartão de crédito não informado.');
			}

			const queryParams = new URLSearchParams(window.location.search);

			const customTrialDays = queryParams.get('t') || null;

			return await createSubscriptionApi.mutateAsync({
				// `currentSubscription` (= `opened?.subscription ?? subscription`) e
				// não `subscription`: sem isso o aceite feito logo após o `open`
				// mandava id undefined e voltava "Pagamento não encontrado".
				id: currentSubscription?.id as number,
				identifier: searchParams.get('identifier') || undefined,
				recurringPlanId: plan.id,
				cpfCnpj,
				billingTypeId: billingType,
				creditCard: isCreditCard
					? createPaymentData.creditCards?.[0].creditCard
					: undefined,
				// Local, não `finance?.isAddressRequired`: a página do builder pode
				// esconder o endereço só neste checkout, e o local respeita isso.
				address: isAddressRequired
					? {
							...address,
							number: address?.number?.trim()
					  }
					: undefined,
				phone: data.phone,
				email: data.email,
				installmentCount,
				affiliate_code: searchParams.get('affiliate_code') || undefined,
				// Cupom de plataforma aplicado no plano (só Waid Pay v2).
				coupon_code: coupon?.code || undefined,
				customTrialDays: Number(customTrialDays) || undefined,
				click: searchParams.get('click') || undefined,
				sec: {
					turnstile: data.sec?.turnstile || undefined
				}
			});
		}

		if (!currentPayment) {
			throw new Error(Errors.PAYMENT_NOT_FOUND);
		}

		// `id` é o do banco da PLATAFORMA e vem NULO quando a venda nasce no
		// pooled — o que acontece com o banco dela fora. Aí o identificador é o
		// `uuid`, e é por ele que o cpay encontra a venda.
		//
		// Sem esta queda o checkout chamava `/payments/null/confirm`: a compra
		// abria e nunca fechava, e o erro que aparecia na tela era de CARTÃO,
		// escondendo a causa real.
		const paymentId = currentPayment.id ?? currentPayment.uuid;

		// Sem NENHUM dos dois não há o que confirmar. Falhar aqui, com a
		// mensagem certa, em vez de chamar `/payments/undefined/confirm` e
		// receber de volta um erro que fala de outra coisa.
		if (paymentId === null || paymentId === undefined) {
			throw new Error(Errors.PAYMENT_NOT_FOUND);
		}

		return await confirmPaymentApi.mutateAsync({
			...createPaymentData,
			paymentId
		});
	};

	const handleCreditCard = (
		creditCards: CreditCard[],
		address: Address,
		isForeign: boolean,
		cpfCnpj?: string
	) => {
		return creditCards.map(
			async ({ creditCard, flatAmount, isNewCreditCard }) => {
				if (isNewCreditCard) {
					// Fora do try abaixo: a mensagem dele ("verifique os dados do
					// cartão") esconderia que o problema é carregar o pagamento.
					const params = await resolveCheckoutFinance();
					const expiration = creditCard.expiry.split('/');
					const expiryYear = expiration[isForeign ? 0 : 1];
					const expiryMonth = expiration[isForeign ? 1 : 0];

					try {
						const data = await tokenizeCreditCardApi.mutateAsync({
							type: 'card',
							hasAntifraud: params?.isAntifraudEnabled ?? true,
							gateway: params?.gateway,
							card: {
								number: creditCard.number.replaceAll(' ', ''),
								cvv: creditCard.cvv,
								exp_month: Number(expiryMonth),
								exp_year: Number(expiryYear),
								holder_name: creditCard.holderName
							}
						});

						return {
							percentageAmount:
								creditCards.length === 1 ? 100 : undefined,
							flatAmount:
								creditCards.length > 1 ? flatAmount : undefined,
							creditCard: {
								address: isAddressRequired
									? {
											postalcode:
												address.postalcode.replace(
													/\D/g,
													''
												),
											country: address.country,
											state: address.state,
											city: address.city,
											province: address.province,
											address: address.address,
											number: address.number?.trim()
									  }
									: undefined,
								cpfCnpj,
								expiryMonth,
								expiryYear,
								holderName: creditCard.holderName,
								number: data?.card.last_four_digits,
								token: data?.id
							}
						};
					} catch (e) {
						console.error(e);
						// Cartão vencido / dado recusado: o comprador vê o motivo, não a mensagem genérica.
						if (isCheckoutInputError(e)) throw e;
						// O erro de cima perde o status; o original vai ao Sentry (só se for do servidor).
						reportCheckoutError(
							e,
							'card',
							'Não foi possível registrar o cartão de crédito.'
						);
						throw new Error(
							'Não foi possível registrar o cartão de crédito. Verifique os dados e tente novamente'
						);
					}
				} else {
					return {
						percentageAmount: flatAmount ? undefined : 100,
						flatAmount: flatAmount,
						creditCard: {
							// O <select> entrega o id como TEXTO e o pay valida
							// `@IsNumber()`: sem converter, a compra com cartão
							// salvo voltava "creditCard.id must be a number".
							id: Number(creditCard.id)
						}
					};
				}
			}
		);
	};

	useEffect(() => {
		localStorage.removeItem('access_token');
	}, []);

	return {
		handle
	};
};
